import { useEffect, useRef, useState } from 'react';

// usePullToRefresh — touch pull-to-refresh for the page (window scroll).
//
// Attaches passive touch listeners; when the user drags down while already
// scrolled to the top, it tracks a resisted pull distance and, past the
// threshold, calls onRefresh() (awaited) while showing a spinner. Data on
// these screens is already live via onSnapshot — this is a manual re-sync
// gesture with feedback, the expected mobile affordance.
//
// Returns { pull, refreshing } for rendering an indicator.
export default function usePullToRefresh(onRefresh, opts = {}) {
  const threshold = opts.threshold || 70;
  const disabled  = opts.disabled || false;

  const [pull, setPullState]     = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startY        = useRef(null);
  const pullRef       = useRef(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    if (disabled) return undefined;

    const scroller = document.scrollingElement || document.documentElement;
    const atTop = () => (scroller.scrollTop || window.scrollY || 0) <= 0;
    const setPull = (v) => { pullRef.current = v; setPullState(v); };

    function onStart(e) {
      if (refreshingRef.current || !atTop()) { startY.current = null; return; }
      startY.current = e.touches[0].clientY;
    }
    function onMove(e) {
      if (startY.current == null || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && atTop()) {
        // Rubber-band resistance; cap a little past threshold.
        const p = Math.min(threshold * 1.5, dy * 0.5);
        setPull(p);
        if (p > 4 && e.cancelable) e.preventDefault();
      } else if (dy <= 0) {
        setPull(0);
      }
    }
    async function onEnd() {
      if (startY.current == null) return;
      startY.current = null;
      if (pullRef.current >= threshold) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPull(threshold);
        try { await onRefresh(); } catch (e) { /* swallow — indicator still clears */ }
        refreshingRef.current = false;
        setRefreshing(false);
        setPull(0);
      } else {
        setPull(0);
      }
    }

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove',  onMove,  { passive: false });
    window.addEventListener('touchend',   onEnd,   { passive: true });
    window.addEventListener('touchcancel', onEnd,  { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove',  onMove);
      window.removeEventListener('touchend',   onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [onRefresh, threshold, disabled]);

  return { pull, refreshing };
}
